import { V1_TO_V2_MONO_TYPOGRAPHY, V1_TO_V2_TYPOGRAPHY } from './v1-to-v2-typography-mapping-data';
import type { MonoTypographyMapping, TypographyMapping, TypographyMappingType, V1MonoTypographyKey, V1TypographyKey, V2TypographyKey } from './v1-to-v2-typography-mapping-data';
export type { MappedV2TypographyKey, MonoTypographyMapping, TypographyMapping, TypographyMappingType, V1MonoTypographyKey, V1ToV2MonoTypographyMap, V1ToV2TypographyMap, V1TypographyKey, V2TypographyKey } from './v1-to-v2-typography-mapping-data';
export { V1_TO_V2_MONO_TYPOGRAPHY, V1_TO_V2_TYPOGRAPHY } from './v1-to-v2-typography-mapping-data';

// ---------------------------------------------------------------------------
// Standard mapping helpers
// ---------------------------------------------------------------------------

/**
 * Map a Base 1.0 typography key to its full v2 mapping descriptor.
 *
 * @example
 * mapV1TypographyToV2('headingSmall')
 * // → { v2: 'headlineXlBold', mapping: 'direct' }
 *
 * mapV1TypographyToV2('displayLarge')
 * // → { v2: 'headlineXlBold', mapping: 'nearest', notes: '96px → 24px' }
 */
export const mapV1TypographyToV2 = (v1Key: V1TypographyKey | string): TypographyMapping | undefined => v1Key in V1_TO_V2_TYPOGRAPHY ? V1_TO_V2_TYPOGRAPHY[(v1Key as V1TypographyKey)] : undefined;

/** Return only the v2 key for a given v1 standard key, discarding metadata. */
export const mapV1TypographyKeyToV2 = (v1Key: V1TypographyKey | string): V2TypographyKey | undefined => mapV1TypographyToV2(v1Key)?.v2;

/** Return all standard v1 keys whose mapping is of the given type. */
export const getV1KeysByMappingType = (type: TypographyMappingType): V1TypographyKey[] => (Object.entries(V1_TO_V2_TYPOGRAPHY) as [V1TypographyKey, TypographyMapping][]).filter(([, m]) => m.mapping === type).map(([k]) => k);

// ---------------------------------------------------------------------------
// Mono mapping helpers
// ---------------------------------------------------------------------------

/**
 * Map a Base 1.0 mono typography key to its full v2 mapping descriptor.
 * The descriptor includes `standardKey` (the non-mono counterpart) and
 * `monoTransform: true` as a reminder to apply the platform mono transform:
 *   fontFamily → 'UberMoveMono', letterSpacing → 0, fontWeight → nearest available.
 *
 * @example
 * mapV1MonoTypographyToV2('monoHeadingSmall')
 * // → { v2: 'headlineXlBold', mapping: 'direct', standardKey: 'headingSmall', monoTransform: true }
 */
export const mapV1MonoTypographyToV2 = (v1Key: V1MonoTypographyKey | string): MonoTypographyMapping | undefined => v1Key in V1_TO_V2_MONO_TYPOGRAPHY ? V1_TO_V2_MONO_TYPOGRAPHY[(v1Key as V1MonoTypographyKey)] : undefined;

/** Return only the v2 key for a given v1 mono key, discarding metadata. */
export const mapV1MonoTypographyKeyToV2 = (v1Key: V1MonoTypographyKey | string): V2TypographyKey | undefined => mapV1MonoTypographyToV2(v1Key)?.v2;

/** Return all mono v1 keys whose mapping is of the given type. */
export const getV1MonoKeysByMappingType = (type: TypographyMappingType): V1MonoTypographyKey[] => (Object.entries(V1_TO_V2_MONO_TYPOGRAPHY) as [V1MonoTypographyKey, MonoTypographyMapping][]).filter(([, m]) => m.mapping === type).map(([k]) => k);